import { computed, defineComponent, ref, watch } from "vue";
import * as KlipApi from "@/api/klip-api.proxy";
import {escape} from "html-escaper";
import {Mri} from "@/app/mri/shared/services/services";
import {isEmpty} from "lodash-es";
import {useForm} from "vee-validate";
import {
    klValidateRules,
    klValidationFields,
    ValidationFieldsType
} from "@/app/shared/components/kl-form-fields/kl-field-validation-util";
import {useKlModalEventsUtil} from "@/app/shared/helpers/kl-modal-events-util";
import * as yup from 'yup';
import {createAddressSchema} from '@/app/shared/components/kl-address-form/kl-address-form';
import {toTypedSchema} from '@vee-validate/yup';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlOrganisationActor',
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Object as () => KlipApi.OrganisatieAthumiData,
            required: false,
            default: () => ({}),
        },
        showEmail : {
            type: Boolean,
            default: true
        },
        hasAthumiId : {
            type: Boolean,
            default: true,
        },
        kboNumber : {
            type: String,
            required: false,
            default: undefined,
        },
        organisationName : {
            type: String,
            required: false,
            default: undefined,
        },
        routeAdminOrganisationName : {
            type: String,
            required: false,
            default: undefined,
        }
    },
    setup(props, { emit }) {

        const schema = yup.object({
            vatNumber: yup.string().required().max(20).kl_vatNumberValid().label('BTW-nummer'),
            useEmail: yup.boolean().notRequired(),
            emailAddress: yup.string().notRequired().label('Facturatie e-mailadres')
                .when('useEmail', { is: true, then: schema => schema.required().email()}),
            actorAddress: createAddressSchema(),
        });

        const editActorModal = 'edit-actor-modal';

        const disableSubmit = computed((): boolean => !form.meta.value.valid);

        const isSending = ref<boolean>(false);
        const noVatNumberPresent = ref<boolean>(true);
        const athumiOrganisationVat = ref<string>(null);
        const athumiOrganisation = ref<string>(null);
        const subOrgRouteName = ref<string>(null);

        const form = useForm({
            validationSchema: toTypedSchema(schema),
            initialValues: {
                useEmail: props.showEmail,
                actorAddress: { countryCode: 'BE' }
            }
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const email = fields.emailAddress.getValue<string>();
        const vatNumber = fields.vatNumber.getValue<string>();
        const actorAddress = fields.actorAddress.getValue<Mri.IAddress>();

        const athumiOrganisationContactAddress = ref<string>(null);
        const initValue = ref<KlipApi.OrganisatieAthumiData>(null);

        const editActor = () => {
            editActorModalEvents.triggerModalToggle();
            edit.value = true;
        }

        const saveActor = () => {

            isSending.value = true;
            const value = KlipApi.OrganisatieAthumiData.fromJS(initValue.value);

            value.straat = actorAddress.value.street;
            value.countryCode = actorAddress.value.countryCode;
            value.emailAdressen = [email.value];
            value.nummer = actorAddress.value.streetNumber;
            value.bus = actorAddress.value.box;
            value.gemeente = actorAddress.value.city;
            value.postcode = actorAddress.value.zip;
            value.vatNumber = vatNumber.value;

            emit('update:modelValue', value);

            _setActor(value);

            edit.value = false;
            editActorModalEvents.triggerModalToggle();
        }

        const cancelEdit = () => {
            isSending.value = false;
            edit.value = false;
        }

        const edit = ref<boolean>(false);

        const _setKboNumber = (kboNumber: string) => {
            if (!isEmpty(kboNumber)) {
                vatNumber.value = 'BE' +  kboNumber;
            }
            noVatNumberPresent.value = isEmpty(kboNumber);
        }

        const _setActor = (newActor: KlipApi.OrganisatieAthumiData) => {
            if (!isEmpty(newActor) && newActor.hasValidAthumiId) {

                initValue.value = newActor;

                const athumiData = newActor;
                const street = `${athumiData.straat ? escape(athumiData.straat) : ''}${athumiData.straat && athumiData.nummer ? ` ${escape(athumiData.nummer)}` : ''}${athumiData.bus ? ` ${escape(athumiData.bus)}` : ''}<br>`;
                const city = `${athumiData.postcode && athumiData.gemeente ? `${escape(athumiData.postcode)} ` : ''}${athumiData.gemeente ? escape(athumiData.gemeente) : ''}`;

                athumiOrganisationContactAddress.value = `${street}${city}`;
                athumiOrganisation.value = `${athumiData.organisatieNaam} - ${props.kboNumber}`;
                athumiOrganisationVat.value = athumiData.vatNumber;
                noVatNumberPresent.value = !athumiData.vatNumber;

                actorAddress.value = {
                    street: athumiData.straat,
                    streetNumber: athumiData.nummer,
                    box: athumiData.bus,
                    city: athumiData.gemeente,
                    zip: athumiData.postcode,
                    countryCode: athumiData.countryCode
                };

                email.value = athumiData.emailAdressen[0];
                vatNumber.value = athumiData.vatNumber;
            }
            else
            {
                initValue.value = null;

                athumiOrganisationContactAddress.value = '';
                athumiOrganisation.value = `${props.organisationName} - ${props.kboNumber}`;
                athumiOrganisationVat.value = '';
                email.value = null;
                vatNumber.value = null;

                actorAddress.value = {
                    street: '',
                    streetNumber: '',
                    box: '',
                    city: '',
                    zip: '',
                    countryCode: 'BE',
                };
            }
        }

        watch(
            () => props.modelValue,
            (val: KlipApi.OrganisatieAthumiData) => {
                _setActor(val);
            },
            { immediate: true, deep: true })

        watch(
            () => props.kboNumber,
            (val: string) => {
                _setKboNumber(val);
            },
            { immediate: true, deep: true })

        watch(
            () => props.routeAdminOrganisationName,
            (val: string) => {
                subOrgRouteName.value = val;
            },
            { immediate: true, deep: true })

        const editActorModalEvents = useKlModalEventsUtil(editActorModal, {
            onClosed() {
                isSending.value = false;
                edit.value = false;
            },
        });

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }
        const onSubmit = form.handleSubmit(saveActor, _onInvalidSubmit);

        return {
            noVatNumberPresent,
            vatNumber,
            editActorModal,
            athumiOrganisationContactAddress,
            athumiOrganisationVat,
            subOrgRouteName,
            athumiOrganisation,
            editActor,
            cancelEdit,
            actorAddress,
            email,
            onSubmit,
            edit,
            disableSubmit,
            isSending,

            fields,
        };
    }
});
